// Findings (spec §4, §6 E2/E3): which source types feed a rule, which settings a rule reads, Turkish labels and the closing sentences.
// Pure, no I/O: api/ writes the data, web/ and the PDF show it with the same code (ADR-0005 "Biçimlendirme").
import type { BoxName } from "./sources";
import { SOURCE_BOXES } from "./sources";
import type { BoxStatus, BoxWarning, CloseReason, FindingKind, RuleCode, RunSummary, SettingChange, Severity } from "./api";
import type { SourceType } from "./rows";
import { formatKg, formatNumber } from "./versions";

/** Spec §4 "Girdi" column. `DATA_*` findings use their own source type (spec §5). */
export const RULE_INPUTS: Record<RuleCode, SourceType[]> = {
  R1: ["GUMRUK_ITH", "ERP_STOK", "GTIP_MALZEME"],
  R1x: ["GUMRUK_IHR", "ERP_STOK", "GTIP_MALZEME"],
  R2: ["ERP_STOK"],
  R3: ["ERP_STOK", "RECETE"],
  R4: ["ERP_STOK", "GUMRUK_IHR", "GTIP_MALZEME"],
  R5: ["GUMRUK_ITH", "GUMRUK_IHR", "DIIB", "RECETE"],
  R6: ["KAMERA", "GUVENLIK"],
  R7: ["GUMRUK_ITH", "MUHASEBE"],
};

/** ADR-0006 "Kapanış nedeni": the settings a rule reads (R1/R1x → r1, R3 → r3, R5 → r5, R6 → r6 and camera_gate). */
export function readsSetting(rule: RuleCode, key: string): boolean {
  switch (rule) {
    case "R1": case "R1x": return key === "tolerance.r1_quantity_kg";
    case "R3": return key === "tolerance.r3_deviation_pct";
    case "R5": return key === "tolerance.r5_forecast_window_days";
    case "R6": return key === "tolerance.r6_outage_threshold_min" || key.startsWith("camera_gate.");
    default: return false;
  }
}

export const severityLabel: Record<Severity, string> = { critical: "KRİTİK", warning: "UYARI", info: "BİLGİ" };
/** E6 değişiklik listesi kural etiketi (ham kod ekranda görünmez). */
export const ruleLabel = { DATA_CHANGED: "Kayıt değişti", DATA_REMOVED: "Kayıt kaldırıldı" } as const;
export const findingStatusLabel = { open: "Açık", closed: "Kapandı" } as const;
export const kindLabel: Record<FindingKind, string> = {
  quantity_mismatch: "Miktar farkı", no_erp_receipt: "ERP girişi yok", no_hs_mapping: "GTİP–malzeme eşleşmesi yok", unreferenced_receipt: "Referanssız giriş",
  unreferenced_shipment: "Referanssız sevk", negative_balance: "Bakiye eksiye düşüyor", bom_deviation: "Reçete sapması",
  negative_product_stock: "Mamul stoku eksiye düşüyor", forecast_shortfall: "Öngörülen açık", over_fulfilment: "Gerçekleşme %100'ü aştı",
  camera_outage: "Kamera kaydı kesintisi", no_accounting_entry: "Muhasebe kaydı yok",
};

/** R6 basis (spec §4): tied to the company's authorisation; without one the sentence names no article. Article texts: scope §3.5, to be confirmed (spec §12). */
const AUTHORIZATION_BASIS: Record<string, string> = {
  export_local_clearance: "ihracatta yerinde gümrükleme yetkisi (GİK m.67/1)",
  authorised_consignor: "izinli gönderici yetkisi (GİK m.84/2)",
};
export function cameraBasis(authorizations: readonly string[]): string {
  const bases = authorizations.map((a) => AUTHORIZATION_BASIS[a]).filter(Boolean);
  return bases.length ? `Kayıt sürekliliğinde boşluk. Kamera hükmü firmanın yetkisine bağlıdır: ${bases.join("; ")}.` : "Kayıt sürekliliğinde boşluk.";
}

// ---- E2 box status ----

type OpenFinding = { rule: string; detail: unknown };
/** Spec §6 E2: a box warns only for open R1 / R1x / R6 findings whose inputs include one of the box's source types. */
export function boxStatus(box: BoxName, open: OpenFinding[]): BoxStatus {
  const types = SOURCE_BOXES[box];
  const hits = open.filter((f) => (f.rule === "R1" || f.rule === "R1x" || f.rule === "R6") && RULE_INPUTS[f.rule].some((t) => types.includes(t)));
  if (!hits.length) return { status: "current", warnings: [] };
  const subject = (f: OpenFinding) => String((f.detail as { subject?: unknown }).subject ?? "");
  const warnings: BoxWarning[] = hits.map((f) => ({ rule: f.rule as BoxWarning["rule"], record: subject(f) }));
  const days = new Set(hits.filter((f) => f.rule === "R6").flatMap((f) => (f.detail as { values?: { days?: string[] } }).values?.days ?? []));
  const parts: string[] = [];
  const r1 = warnings.filter((w) => w.rule !== "R6");
  if (r1.length) parts.push(`Beyanname ↔ ERP girişi uyuşmuyor (${r1.map((w) => w.record).join(", ")})`);
  if (days.size) parts.push(`${days.size} gün eksik kayıt`);
  return { status: "warning", warning: parts.join(" · "), warnings, ...(days.size ? { missingDays: days.size } : {}) };
}

// ---- closing sentences (spec §4) ----

const show = (v: unknown) => (typeof v === "number" ? formatNumber(v) : v === null || v === undefined ? "yok" : String(v));
/** "R3 toleransı %1 → %2" */
export function settingChangeText(c: SettingChange): string {
  const [o, n] = [show(c.old), show(c.new)];
  if (c.key === "tolerance.r1_quantity_kg") return `R1 toleransı ${o} kg → ${n} kg`;
  if (c.key === "tolerance.r3_deviation_pct") return `R3 toleransı %${o} → %${n}`;
  if (c.key === "tolerance.r5_forecast_window_days") return `R5 öngörü penceresi ${o} gün → ${n} gün`;
  if (c.key === "tolerance.r6_outage_threshold_min") return `R6 kesinti eşiği ${o} dk → ${n} dk`;
  if (c.key.startsWith("camera_gate.")) return `kamera-kapı eşlemesi ${c.key.slice("camera_gate.".length)}: ${o} → ${n}`;
  return `${c.key}: ${o} → ${n}`;
}

/** `fileName` is the raw file name (null for a manual entry); the label comes from `origin`. */
export type CloseUpload = { origin?: "file" | "manual"; fileName: string | null; justification: string | null };
/**
 * Spec §4 closing sentences. `uploads` maps upload no to its file name and justification (read from `uploads`).
 * upload → "Kapandı — veri düzeltmesiyle, yükleme #12 `f.xlsx`, gerekçe: '…'"; setting → "Kapandı — ayar değişikliğiyle, R3 toleransı %1 → %2";
 * both → "Kapandı — yükleme #12 `f.xlsx` ve ayar değişikliği (R3 toleransı %1 → %2)"; then app version; then run date.
 */
export function closeReasonText(r: CloseReason, uploads: ReadonlyMap<number, CloseUpload>): string {
  if (r.nextUploadId !== undefined) return `Kapandı — sonraki ${uploads.get(r.nextUploadId)?.origin === "manual" ? "elle giriş" : "yükleme"} #${r.nextUploadId}`;
  const ids = r.uploadIds ?? [];
  const manual = (id: number) => uploads.get(id)?.origin === "manual";
  const label = (id: number) => `#${id} \`${uploads.get(id)?.fileName ?? "?"}\``;
  const settings = (r.settings ?? []).map(settingChangeText).join(", ");
  // spec §4/§5: a manual entry has no file name, its label stands in: "elle giriş #13"
  const one = (id: number) => (manual(id) ? `elle giriş #${id}` : `yükleme ${label(id)}`);
  const upl = ids.length === 1 ? one(ids[0]!) : ids.some(manual) ? ids.map(one).join(", ") : `yüklemeler ${ids.map(label).join(", ")}`;
  if (ids.length && settings) return `Kapandı — ${upl} ve ayar değişikliği (${settings})`;
  if (ids.length) {
    const why = ids.map((id) => uploads.get(id)?.justification).filter(Boolean);
    return `Kapandı — veri düzeltmesiyle, ${upl}${why.length ? `, gerekçe: ${why.map((w) => `'${w}'`).join("; ")}` : ""}`;
  }
  if (settings) return `Kapandı — ayar değişikliğiyle, ${settings}`;
  if (r.appVersions) return `Kapandı — kural değişikliği (sürüm ${r.appVersions[0]} → ${r.appVersions[1]})`;
  return "Kapandı — koşu tarihi (öngörü yeniden hesaplandı)";
}

/** E3 top line (spec §4): "İşlenen: 7 beyanname kalemi (4 ithalat, 3 ihracat) · 1.840 stok hareketi · süre 0,4 sn. Eşleşen 6 · Eşleşmeyen 1 · Toplam fark 3.000 kg". */
export function runSummaryLine(s: Pick<RunSummary, "importItems" | "exportItems" | "stockMovements" | "durationMs" | "matchedCount" | "unmatchedCount" | "totalDiffKg">): string {
  const sec = formatNumber(Math.round(s.durationMs / 100) / 10);
  return `İşlenen: ${formatNumber(s.importItems + s.exportItems)} beyanname kalemi (${formatNumber(s.importItems)} ithalat, ${formatNumber(s.exportItems)} ihracat) · ${formatNumber(s.stockMovements)} stok hareketi · süre ${sec} sn. Eşleşen ${formatNumber(s.matchedCount)} · Eşleşmeyen ${formatNumber(s.unmatchedCount)} · Toplam fark ${formatKg(s.totalDiffKg)} kg`;
}
